import PropTypes from 'prop-types';
import styled from 'styled-components';
import React from 'react';

import '@yext/search-ui-react/bundle.css';

import searchIconDefault from '../../assets/icons/search/search-magnifying-glass-grey.svg';
import searchIconSelected from '../../assets/icons/search/search-magnifying-glass-red.svg';

import { animation, colors, fontSizes } from '../../ui-components';
import { SearchBar } from '@yext/search-ui-react';

const propTypes = {
  handleSearch: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

/**
 * This is our custom searchbar component that is essentially just a wrapper around Yext's pre-built
 * SearchBar component.
 * @component CustomSearchBar
 *
 * @prop {function} handleSearch - required function that is executed when a search is executed
 * @prop {String} placeholder - the placeholder string displayed on the searchbar
 * 
 * @return {JSX}
 */
const CustomSearchBar = props => {
  const { handleSearch, placeholder = 'Search' } = props;

  return (
    <SearchBarWrapper>
      <SearchBar
        customCssClasses={{
          searchBarContainer: 'search-bar-container',
          inputElement: 'search-input-element',
          inputDivider: 'search-input-divider',
          clearButton: 'search-clear-button',
          searchButton: 'search-search-button',
          searchButtonContainer: 'search-search-button-container',
          focusedOption: 'search-focused-option',
          recentSearchesIcon: 'search-recent-searches-icon',
          recentSearchesOption: 'search-recent-searches-option',
          recentSearchesNonHighlighted: 'search-recent-searches-non-highlighted',
          verticalLink: 'search-vertical-link',
          verticalDivider: 'search-vertical-divider',
          entityPreviewsDivider: 'search-entity-preview-divider',
          option: 'search-autocomplete-result-option',
          icon: 'search-autocomplete-result-icon',
          highlighted: 'search-autocomplete-result-highlighted',
          nonHighlighted: 'search-autocomplete-result-non-highlighted'
        }}
        hideRecentSearches
        placeholder={placeholder}
        onSearch={handleSearch}
      />
    </SearchBarWrapper>
  );
};

CustomSearchBar.propTypes = propTypes;

export default CustomSearchBar;

/**
 * !Styling the SearchBar!
 * Out-of-the-box, Yext provides an option to apply custom CSS classes to the elements within the
 * Searchbar component. The issue is that it does not provide access to every element which is why
 * you see some crazy CSS selectors below. Luckily, most of the major elements have custom classes
 * applied to them. You can see the full list right above here. If you're trying to look for
 * something specific, you'll sadly just have to keep clicking through the element tree to find it.
 *
 * YOU SHOULDN'T NEED TO ADJUST THE STYLING WITHIN THIS COMPONENT! Ideally, you should only ever
 * need apply additional styling to the SearchBar from wherever it is being used. See the header and
 * search components for examples on how this is being done. Only global searchbar styling updates
 * should happen here.
 */

const SearchBarWrapper = styled.div`
  display: flex;

  .search-bar-container {
    flex: 1;
    margin: 0;
    width: 100%;

    // Targets only the child div in .search-bar-container
    & > div {
      border-radius: 0px;
      border: none;
      display: flex;
      flex-direction: column;

      // Targets the div that contains the input field for the search bar
      & > div:first-child {
        // In order for the icon to be positioned correctly, this seemed to be the easiest way to add it
        // So, instead of changing the color of the SVG on focus, it changes the SVG itself
        ::before {
          content: ' ';
          background-image: url('${searchIconDefault}');
          width: 24px;
          height: 24px;
          transition: background-image ${animation.general.durationAndTiming};
        }

        :focus-within:before {
          background-image: url('${searchIconSelected}');
        }
      }

      // Targets the div that contains the autocomplete results
      & > div:nth-child(3) {
        // Top padding is 12px here, the other 12px comes from the first autocomplete result
        padding: 12px 0 0 0;
        display: flex;
        flex-direction: column;

        // Remove all padding applied to the numerous child divs
        div {
          padding: 0;
        }

        // Add back some padding to the result options to space them out a bit
        .search-autocomplete-result-option {
          padding: 12px 0 12px 44px;
        }

        // Yext does not provide a way to limit/alter the of autocomplete results, so we have to use CSS.
        // This will hide all but the first 6 results.
        & > div:nth-child(n + 7) {
          display: none;
        }
      }
    }
  }

  .search-input-element {
    /* Body Copy/Desktop/Medium */
    font-family: Lato, sans-serif;
    font-size: ${fontSizes.paragraph.medium.fontSize};
    font-style: normal;
    font-weight: 400;
    line-height: ${fontSizes.paragraph.medium.lineHeight};
    color: ${colors.midnightBlack};

    padding: 0 0 0 20px;
    height: 26px;
  }

  .search-clear-button {
    margin: 0;
    // Cannot override the SVG used here, so the dimensions are not 24px like shown in the design
    // 16px looked to be the closet to the design
    width: 16px;
    height: 16px;
    svg path {
      fill: ${colors.gray2};
    }
  }

  .search-input-divider {
    margin: 24px 0 0 0;
    border-color: ${colors.gray3};
  }

  .search-autocomplete-result-highlighted {
    font-weight: 700;
  }

  .search-autocomplete-result-option {
    color: ${colors.midnightBlack};
  }

  .search-search-button-container,
  .search-vertical-divider,
  .search-autocomplete-result-icon {
    display: none;
  }
`;
